// src/pages/AltaWizard.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { precheckAlta, altaSocioV2 } from "../services/sociosApi";
import { authFetch } from "../services/auth";
import { isEmpleado } from "../services/auth";
import { slugify, folderNameFromNameOrDni } from "../utils/slugify";
import StepConductores from "../components/AltaWizard/StepConductores";

const H = (s) => s?.replace(/_/g, " ") || "";

/* ---------------- Agrupar docs por bloques (tu función original) ---------------- */
function agruparDocumentosPorBloque(plan) {
  const docs = Array.isArray(plan?.documentos_sugeridos)
    ? plan.documentos_sugeridos
    : [];

  const grupos = {
    CAPITALIZACION: [],
    DATOS_PERSONALES: [],
    CONDUCTOR: [],
    VEHICULO: [],
    COOPERATIVA: [],
  };

  for (const d of docs) {
    const catRaw = (d.categoria || "").toUpperCase();
    const cat = catRaw.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    if (cat === "CAPITALIZACION") {
      grupos.CAPITALIZACION.push(d);
    } else if (cat === "DATOS_PERSONALES") {
      grupos.DATOS_PERSONALES.push(d);
    } else if (cat.startsWith("CONDUCTOR")) {
      // "CONDUCTOR" o "CONDUCTORES"
      grupos.CONDUCTOR.push(d);
    } else if (cat === "DOCUMENTOS_VEHICULO" || cat.startsWith("DOCUMENTOS_VEHICULO")) {
      grupos.VEHICULO.push(d);
    } else if (cat === "DOCUMENTOS_COOPERATIVA") {
      grupos.COOPERATIVA.push(d);
    }
  }

  return grupos;
}

function Badge({ children, tone = "gray" }) {
  const tones = {
    gray: "bg-gray-100 text-gray-700",
    green: "bg-green-100 text-green-800",
    amber: "bg-amber-100 text-amber-800",
    blue: "bg-blue-100 text-blue-800",
  };
  return (
    <span className={`inline-block text-xs px-2 py-0.5 rounded ${tones[tone] || tones.gray}`}>
      {children}
    </span>
  );
}

/* --------- Inyección cliente: carpetas + docs por cada conductor --------- */
/* Crea:
   CONDUCTORES/{Nombre Apellidos}/
     DATOS_PERSONALES/
     NOMINAS/
   y sugiere documentos en CONDUCTORES/{Nombre Apellidos}/DATOS_PERSONALES
*/

function augmentPlanWithConductores(plan, { flags, socio, conductores }) {
  const out = { ...(plan || {}) };
  out.documentos_sugeridos = Array.isArray(out.documentos_sugeridos)
    ? [...out.documentos_sugeridos]
    : [];
  out.carpetas_a_crear = Array.isArray(out.carpetas_a_crear)
    ? [...out.carpetas_a_crear]
    : [];

  // Carpetas por cada conductor (incluye titular si conduce)
  const folders = buildConductorFolders({ flags, socio, conductores });
  for (const f of folders) {
    if (!out.carpetas_a_crear.includes(f)) out.carpetas_a_crear.push(f);
  }

  // Sugerencias de documentos bajo CONDUCTORES/<slug>/DATOS_PERSONALES
  const rol = flags?.rol || "FACTURA_Y_CONDUCE";
  const titularConduce = rol !== "SOLO_FACTURA";
  const list = [];
  if (titularConduce) list.push({ ...socio, esTitular: true });
  (conductores || []).forEach((c) => list.push({ ...c, esTitular: false }));

    list.forEach((c, i) => {
    const name = conductorFolderName(c, i); // ← visible
    const baseSub = `${name}/DATOS_PERSONALES`; // CONDUCTORES/<Nombre Apellidos>/DATOS_PERSONALES

    const docsPersona = [
      { nombre_sugerido: "DNI o NIE.pdf", requiere_caducidad: false },
      { nombre_sugerido: "Permiso de conducción.pdf", requiere_caducidad: true },
      { nombre_sugerido: "CAP.pdf", requiere_caducidad: true },
      { nombre_sugerido: "Tarjeta sanitaria.pdf", requiere_caducidad: false },
      { nombre_sugerido: "Certificado digital.pfx", requiere_caducidad: false },
    ];

    docsPersona.forEach((d) => {
      const doc = {
        categoria: "CONDUCTORES",
        subcarpeta: baseSub,
        nombre_sugerido: d.nombre_sugerido,
        requiere_caducidad: !!d.requiere_caducidad,
      };
      const dup = out.documentos_sugeridos.find(
        (x) =>
          x.categoria?.toUpperCase() === "CONDUCTORES" &&
          (x.subcarpeta || "") === baseSub &&
          x.nombre_sugerido === doc.nombre_sugerido
      );
      if (!dup) out.documentos_sugeridos.push(doc);
    });
  });

  return out;
}

// Helpers de carpeta/slug (usar util centralizado)
function conductorFolderName({ nombre, apellidos, dni }, idx) {
  // Visibles: "Nombre Apellidos" o DNI; fallback "Conductor N"
  const visible = folderNameFromNameOrDni(nombre, apellidos, dni);
  if (visible && visible !== "Conductor") return visible;
  return `Conductor ${idx + 1}`;
}

// Construye TODAS las carpetas requeridas para cada conductor
function buildConductorFolders({ flags, socio, conductores }) {
  const rol = flags?.rol || "FACTURA_Y_CONDUCE";
  const titularConduce = rol !== "SOLO_FACTURA";

  const list = [];
  if (titularConduce) list.push({ ...socio, esTitular: true });
  (conductores || []).forEach((c) => list.push({ ...c, esTitular: false }));

  const folders = [];
  list.forEach((c, i) => {
    const name = conductorFolderName(c, i); // ← visible
    // raíz del conductor
    folders.push(`CONDUCTORES/${name}/`);
    // subcarpetas solicitadas
    folders.push(`CONDUCTORES/${name}/DATOS_PERSONALES/`);
    folders.push(`CONDUCTORES/${name}/NOMINAS/`);
  });
  return folders;
}

// Devuelve [ {slug, esTitular} … ] incluyendo al titular si "factura y conduce"
function getConductorList(flags, socio, conductores) {
  const rol = flags?.rol || "FACTURA_Y_CONDUCE";
  const titularConduce = rol !== "SOLO_FACTURA";
  const list = [];

  if (titularConduce) {
    list.push({
      slug: folderNameFromNameOrDni(
        { nombre: socio?.nombre || "", apellidos: socio?.apellidos || "", dni: socio?.dni || "" },
        "TITULAR"
      ),
      esTitular: true,
    });
  }

  for (let i = 0; i < (conductores?.length || 0); i++) {
    const c = conductores[i] || {};
    list.push({
      slug: folderNameFromNameOrDni(
        { nombre: c?.nombre || "", apellidos: c?.apellidos || "", dni: c?.dni || "" },
        `CONDUCTOR_${i + 1}`
      ),
      esTitular: false,
    });
  }
  return list;
}

// Solo las subcarpetas relativas que hay que crear bajo la categoría CONDUCTORES
function getConductorSubfolders(flags, socio, conductores) {
  const list = getConductorList(flags, socio, conductores);
  const subs = [];
  for (const c of list) {
    subs.push(`${c.slug}/DATOS_PERSONALES`);
    subs.push(`${c.slug}/NOMINAS`);
  }
  return subs;
}

// Crea (idempotente) las subcarpetas en el backend
async function crearSubcarpetasConductores(socioId, subcarpetas, authFetchFn) {
  if (!socioId || !Array.isArray(subcarpetas) || subcarpetas.length === 0) return;

  await Promise.allSettled(
    subcarpetas.map((nombre_subcarpeta) =>
      authFetchFn(`/api/documentos/${socioId}/carpeta`, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ categoria: "CONDUCTORES", nombre_subcarpeta }),
      })
    )
  );
}

// Crea directorios en el servidor probando varios endpoints y nombres de propiedad.
// Si tu backend solo acepta uno de ellos, alguno de estos caerá en OK.
async function ensureFoldersOnServer(socioId, folderPaths = []) {
  if (!socioId || !Array.isArray(folderPaths) || folderPaths.length === 0) return;

  const payloadVariants = [
    { paths: folderPaths },
    { carpetas: folderPaths },
    { carpetas_a_crear: folderPaths },
    { folders: folderPaths },
  ];

  const endpoints = [
    `/api/socios/${socioId}/carpetas`,
    `/api/socios/${socioId}/mkdirs`,
    `/api/socios/${socioId}/folders`,
  ];

  for (const body of payloadVariants) {
    for (const url of endpoints) {
      try {
        const r = await authFetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (r.ok) return; // creado ✅
      } catch {
        // probamos siguiente variante
      }
    }
  }
  // Si ninguno existe, no pasa nada: simplemente no se materializan vacías.
}

export default function AltaWizard() {
  const navigate = useNavigate();
  const puede = isEmpleado();

  // 4 pasos: 1 Preguntas, 2 Conductores, 3 Documentos, 4 Datos/Creación
  const [step, setStep] = useState(1);

  // Flags / preguntas del flujo
  const [flags, setFlags] = useState({
    capitaliza: false,
    capitaliza_madrid: false,

    // Facturación
    rol: "FACTURA_Y_CONDUCE", // "FACTURA_Y_CONDUCE" | "SOLO_FACTURA"
    cert_terceros_paises: false,

    // Vehículo
    vehiculo_procedencia: "", // "COMPRA" | "ALQUILER_RENTING"
    compra_modalidad: "", // "FACTURA_DIRECTA" | "TRAMITE_SIMPLE" | "DOBLE_TRAMITE"
    alquiler_tipo: "", // "ALQUILER" | "RENTING"

    // Entrada (informativo en precheck)
    entrada_pagada: false,
  });

  // Datos del socio (mínimos para crear ficha)
  const [socio, setSocio] = useState({
    nombre: "",
    apellidos: "",
    correo: "",
    telefono: "",
    direccion: "",
    dni: "",
  });

  // Conductores extra (array de objetos). El titular se añadirá si "factura y conduce".
  const [conductores, setConductores] = useState([]);

  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [plan, setPlan] = useState(null); // respuesta de /alta/precheck (posible + inyección)
  const [resAlta, setResAlta] = useState(null);

  useEffect(() => {
    if (!puede) {
      navigate("/", { replace: true });
    }
  }, [puede, navigate]);

  const grupos = useMemo(() => agruparDocumentosPorBloque(plan), [plan]);

  const go = (n) => setStep(n);
  const next = () => setStep((s) => Math.min(4, s + 1));
  const prev = () => setStep((s) => Math.max(1, s - 1));

  async function calcularPlan() {
    setError("");
    setCargando(true);
    try {
      // Pasamos conductores por si el back ya soporta esta semántica
      const p = await precheckAlta({ ...flags, conductores });
      // Inyección en cliente (estructura deseada de conductores)
      const p2 = augmentPlanWithConductores(p, { flags, socio, conductores });
      setPlan(p2);
      setStep(3);
    } catch (e) {
      setError(e?.message || "No se pudo calcular el plan de alta.");
    } finally {
      setCargando(false);
    }
  }

  async function crearFicha() {
  setError("");
  setCargando(true);
  try {
    // Alta normal
    const r = await altaSocioV2({ socio, flags, conductores });

    // Si el back devuelve socio_id, creamos subcarpetas de CONDUCTORES
    if (r?.socio_id) {
      const subcarpetas = getConductorSubfolders(flags, socio, conductores);
      const { authFetch } = await import("../services/auth"); // import dinámico

      await crearSubcarpetasConductores(r.socio_id, subcarpetas, authFetch);

      alert("Ficha creada correctamente.");
      navigate(`/socios/${r.socio_id}`, { replace: true });
      return;
    }

    // Fallback (por si acaso)
    setResAlta(r);
    setStep(4);
  } catch (e) {
    setError(e?.message || "No se pudo crear la ficha.");
  } finally {
    setCargando(false);
  }
}

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold">🧭 Alta de socio (Wizard)</h1>
          <button
            className="text-sm text-blue-700 hover:underline"
            onClick={() => navigate("/", { replace: true })}
          >
            ← Volver
          </button>
        </div>

        {/* Stepper (1..4) manteniendo estilo */}
        <div className="flex items-center justify-between mb-6">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="flex-1 flex items-center">
              <div
                className={`w-8 h-8 flex items-center justify-center rounded-full mr-2
                ${step >= n ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-600"}`}
              >
                {n}
              </div>
              <div
                className={`text-sm font-medium mr-4 ${step >= n ? "text-blue-700" : "text-gray-500"}`}
              >
                {n === 1 && "Preguntas"}
                {n === 2 && "Conductores"}
                {n === 3 && "Documentos por bloques"}
                {n === 4 && "Datos y creación"}
              </div>
              {n < 4 && <div className="flex-1 h-[2px] bg-gray-200" />}
            </div>
          ))}
        </div>

        {error && (
          <div className="mb-4 p-3 rounded bg-red-50 text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* STEP 1: Preguntas guiadas (tu contenido original) */}
        {step === 1 && (
          <div className="space-y-6">
            <fieldset className="border rounded p-4">
              <legend className="px-2 font-semibold">Capitalización</legend>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!flags.capitaliza}
                    onChange={(e) =>
                      setFlags((f) => ({ ...f, capitaliza: e.target.checked }))
                    }
                  />
                  ¿Va a capitalizar?
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    disabled={!flags.capitaliza}
                    checked={!!flags.capitaliza_madrid}
                    onChange={(e) =>
                      setFlags((f) => ({
                        ...f,
                        capitaliza_madrid: e.target.checked,
                      }))
                    }
                  />
                  Comunidad de Madrid
                </label>
              </div>
            </fieldset>

            <fieldset className="border rounded p-4">
              <legend className="px-2 font-semibold">Facturación / Conducción</legend>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="rol"
                    checked={flags.rol === "FACTURA_Y_CONDUCE"}
                    onChange={() =>
                      setFlags((f) => ({ ...f, rol: "FACTURA_Y_CONDUCE" }))
                    }
                  />
                  Factura y conduce
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="rol"
                    checked={flags.rol === "SOLO_FACTURA"}
                    onChange={() =>
                      setFlags((f) => ({ ...f, rol: "SOLO_FACTURA" }))
                    }
                  />
                  Solo factura (conductor distinto)
                </label>
              </div>
              <div className="mt-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!flags.cert_terceros_paises}
                    onChange={(e) =>
                      setFlags((f) => ({
                        ...f,
                        cert_terceros_paises: e.target.checked,
                      }))
                    }
                  />
                  ¿Necesita certificado de terceros países?
                </label>
              </div>
            </fieldset>

            <fieldset className="border rounded p-4">
              <legend className="px-2 font-semibold">Vehículo</legend>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="proc"
                    checked={flags.vehiculo_procedencia === "COMPRA"}
                    onChange={() =>
                      setFlags((f) => ({
                        ...f,
                        vehiculo_procedencia: "COMPRA",
                        alquiler_tipo: "",
                      }))
                    }
                  />
                  Compra directa
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="proc"
                    checked={flags.vehiculo_procedencia === "ALQUILER_RENTING"}
                    onChange={() =>
                      setFlags((f) => ({
                        ...f,
                        vehiculo_procedencia: "ALQUILER_RENTING",
                        compra_modalidad: "",
                      }))
                    }
                  />
                  Alquiler / Renting
                </label>
              </div>

              {flags.vehiculo_procedencia === "COMPRA" && (
                <div className="mt-3 grid sm:grid-cols-3 gap-3">
                  {["FACTURA_DIRECTA", "TRAMITE_SIMPLE", "DOBLE_TRAMITE"].map((m) => (
                    <label className="flex items-center gap-2" key={m}>
                      <input
                        type="radio"
                        name="compra"
                        checked={flags.compra_modalidad === m}
                        onChange={() =>
                          setFlags((f) => ({ ...f, compra_modalidad: m }))
                        }
                      />
                      {H(m)}
                    </label>
                  ))}
                </div>
              )}

              {flags.vehiculo_procedencia === "ALQUILER_RENTING" && (
                <div className="mt-3 grid sm:grid-cols-2 gap-3">
                  {["ALQUILER", "RENTING"].map((m) => (
                    <label className="flex items-center gap-2" key={m}>
                      <input
                        type="radio"
                        name="alquiler"
                        checked={flags.alquiler_tipo === m}
                        onChange={() =>
                          setFlags((f) => ({ ...f, alquiler_tipo: m }))
                        }
                      />
                      {H(m)}
                    </label>
                  ))}
                </div>
              )}
            </fieldset>

            <fieldset className="border rounded p-4">
              <legend className="px-2 font-semibold">Entrada</legend>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!flags.entrada_pagada}
                  onChange={(e) =>
                    setFlags((f) => ({ ...f, entrada_pagada: e.target.checked }))
                  }
                />
                ¿Entrada pagada? (podrás subir el justificante en “Documentos Cooperativa”)
              </label>
            </fieldset>

            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 rounded border hover:bg-gray-50"
                onClick={() => navigate("/", { replace: true })}
              >
                Cancelar
              </button>
              <button
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                onClick={() => go(2)}
              >
                Continuar →
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Conductores (nuevo) */}
        {step === 2 && (
          <div className="space-y-6">
            <StepConductores
              titular={socio}
              rol={flags.rol}
              conductores={conductores}
              onChange={setConductores}
            />

            <div className="flex justify-between">
              <button className="px-4 py-2 rounded border hover:bg-gray-50" onClick={prev}>
                ← Volver
              </button>
              <button
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                onClick={calcularPlan}
                disabled={cargando}
              >
                {cargando ? "Calculando…" : "Continuar →"}
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: Documentos por bloques (resultado de precheck + inyección conductores) */}
        {step === 3 && (
          <div className="space-y-6">
            {!plan ? (
              <p className="text-gray-500">No hay plan calculado.</p>
            ) : (
              <>
                <div className="text-sm text-gray-600">
                  <div>
                    Versión: <Badge tone="blue">{plan.version || "?"}</Badge>
                  </div>
                  {plan?.entrada?.entrada_pagada ? (
                    <div className="mt-1">
                      <Badge tone="green">Entrada marcada como pagada</Badge>
                    </div>
                  ) : (
                    <div className="mt-1">
                      <Badge tone="amber">Entrada pendiente</Badge>
                    </div>
                  )}
                </div>

                {grupos.CAPITALIZACION.length > 0 && (
                  <section className="border rounded p-4">
                    <h3 className="font-semibold mb-2">Bloque 1 · CAPITALIZACIÓN</h3>
                    <ul className="space-y-1">
                      {grupos.CAPITALIZACION.map((d, i) => (
                        <li key={`cap-${i}`} className="flex items-center gap-2">
                          <span>📄 {d.nombre_sugerido}</span>
                          {d.requiere_caducidad && <Badge tone="amber">Con caducidad</Badge>}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {(grupos.DATOS_PERSONALES.length > 0 || grupos.CONDUCTOR.length > 0) && (
                  <section className="border rounded p-4">
                    <h3 className="font-semibold mb-2">Bloque 2 · FACTURACIÓN / DATOS PERSONALES</h3>

                    {grupos.DATOS_PERSONALES.length > 0 && (
                      <>
                        <h4 className="text-sm font-medium text-gray-700 mb-1">Socio / Titular</h4>
                        <ul className="space-y-1 mb-3">
                          {grupos.DATOS_PERSONALES.map((d, i) => (
                            <li key={`dp-${i}`} className="flex items-center gap-2">
                              <span>📄 {d.nombre_sugerido}</span>
                              {d.subcarpeta && (
                                <span className="text-xs text-gray-500">/ {d.subcarpeta}</span>
                              )}
                              {d.requiere_caducidad && <Badge tone="amber">Con caducidad</Badge>}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}

                    {grupos.CONDUCTOR.length > 0 && (
                      <>
                        <h4 className="text-sm font-medium text-gray-700 mb-1">Conductores</h4>
                        <ul className="space-y-1">
                          {grupos.CONDUCTOR.map((d, i) => (
                            <li key={`cond-${i}`} className="flex items-center gap-2">
                              <span>📄 {d.nombre_sugerido}</span>
                              {d.subcarpeta && (
                                <span className="text-xs text-gray-500">/ {d.subcarpeta}</span>
                              )}
                              {d.requiere_caducidad && <Badge tone="amber">Con caducidad</Badge>}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </section>
                )}

                {grupos.VEHICULO.length > 0 && (
                  <section className="border rounded p-4">
                    <h3 className="font-semibold mb-2">Bloque 3 · DOCUMENTOS VEHÍCULO</h3>
                    <ul className="space-y-1">
                      {grupos.VEHICULO.map((d, i) => (
                        <li key={`vh-${i}`} className="flex items-center gap-2">
                          <span>📄 {d.nombre_sugerido}</span>
                          {d.requiere_caducidad && <Badge tone="amber">Con caducidad</Badge>}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {grupos.COOPERATIVA.length > 0 && (
                  <section className="border rounded p-4">
                    <h3 className="font-semibold mb-2">Bloque 5 · DOCUMENTOS COOPERATIVA</h3>
                    <ul className="space-y-1">
                      {grupos.COOPERATIVA.map((d, i) => (
                        <li key={`coop-${i}`} className="flex items-center gap-2">
                          <span>📄 {d.nombre_sugerido}</span>
                          {d.requiere_caducidad && <Badge tone="amber">Con caducidad</Badge>}
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-gray-500 mt-2">
                      * Aquí subirás también el justificante de la entrada.
                    </p>
                  </section>
                )}
              </>
            )}

            <div className="flex justify-between">
              <button className="px-4 py-2 rounded border hover:bg-gray-50" onClick={prev}>
                ← Volver
              </button>
              <button
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                onClick={() => go(4)}
              >
                Continuar →
              </button>
            </div>
          </div>
        )}

        {/* STEP 4: Datos del socio + Crear ficha (tu paso 3 original) */}
        {step === 4 && (
          <div className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-gray-700">Nombre</label>
                <input
                  className="mt-1 border p-2 rounded w-full"
                  value={socio.nombre}
                  onChange={(e) => setSocio((s) => ({ ...s, nombre: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm text-gray-700">Apellidos</label>
                <input
                  className="mt-1 border p-2 rounded w-full"
                  value={socio.apellidos}
                  onChange={(e) => setSocio((s) => ({ ...s, apellidos: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm text-gray-700">DNI</label>
                <input
                  className="mt-1 border p-2 rounded w-full"
                  value={socio.dni}
                  onChange={(e) => setSocio((s) => ({ ...s, dni: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm text-gray-700">Correo</label>
                <input
                  className="mt-1 border p-2 rounded w-full"
                  type="email"
                  value={socio.correo}
                  onChange={(e) => setSocio((s) => ({ ...s, correo: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm text-gray-700">Teléfono</label>
                <input
                  className="mt-1 border p-2 rounded w-full"
                  value={socio.telefono}
                  onChange={(e) => setSocio((s) => ({ ...s, telefono: e.target.value }))}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm text-gray-700">Dirección</label>
                <input
                  className="mt-1 border p-2 rounded w-full"
                  value={socio.direccion}
                  onChange={(e) => setSocio((s) => ({ ...s, direccion: e.target.value }))}
                />
              </div>
            </div>

            <div className="flex justify-between">
              <button className="px-4 py-2 rounded border hover:bg-gray-50" onClick={prev}>
                ← Volver
              </button>
              <button
                className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                onClick={crearFicha}
                disabled={cargando}
              >
                {cargando ? "Creando…" : "Crear ficha"}
              </button>
            </div>

            {resAlta && (
              <div className="text-sm text-gray-600">
                <div>Resultado alta:</div>
                <pre className="bg-gray-50 p-3 rounded overflow-auto">
                  {JSON.stringify(resAlta, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
